/**
 * Announcements from the team, shell-wide (TMMAPP-0142 U2 item 13a): one read per shell mount, for
 * every person (a guest included; no AI, no wallet). It holds:
 * - the list and its unread count, for the account menu's "What's new" badge and Help & feedback's
 *   Announcements tab (`useAnnouncements()`);
 * - **"What's new"**: the same list in a dialog, opened from the account menu;
 * - **the pop-up**: when the list names a `popupId`, that announcement shows once as a modal on this
 *   app load, never over another modal (a dialog, the drawer or a menu: it waits until none is open,
 *   M6). Dismissing it marks it read.
 * A read is marked once, at once in the badges, then `POST …/{id}/read`. A locked member reads
 * them too (TMMAPI-0186 U1 item 7, TMMAPP-0153 item 18: tmm-api's lock allows `GET` announcements and
 * `POST …/{id}/read`): in Help & feedback's tab and "What's new". The pop-up isn't shown while
 * locked: the locked screen stays the person's way back.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { announcementsApi, type Announcement } from '@/api/announcements';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { useMe } from '@/session/meHooks';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { AnnouncementBody, AnnouncementList } from './AnnouncementList';
import { AnnouncementsContext, type AnnouncementsValue } from './announcementsContext';

const WHATS_NEW_TITLE_ID = 'whats-new-title';
const POPUP_TITLE_ID = 'announcement-popup-title';

/** Another modal is open (a dialog, the drawer, a menu): the pop-up never stacks on it (M6). */
function modalOpen(): boolean {
  return document.querySelector('.MuiModal-root:not(.MuiModal-hidden)') !== null;
}

export function AnnouncementsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const locked = useMe().locked;
  const fullScreen = useDialogFullScreen();
  const [items, setItems] = useState<Announcement[] | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [popupId, setPopupId] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [whatsNew, setWhatsNew] = useState(false);
  const [popup, setPopup] = useState<Announcement | null>(null);
  // The pop-up shows at most once per app load.
  const popupShown = useRef(false);
  const readIds = useRef(new Set<number>());

  const load = useCallback(async () => {
    try {
      const list = await announcementsApi.list();
      setItems(list.items);
      setUnreadCount(list.unreadCount);
      setPopupId(list.popupId);
      setError(null);
    } catch (caught) {
      setError(caught);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => void load());
  }, [load]);

  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  });

  const markRead = useCallback((id: number) => {
    if (readIds.current.has(id)) return;
    readIds.current.add(id);
    const target = itemsRef.current?.find((item) => item.id === id);
    if (target !== undefined && !target.read) {
      setItems((current) =>
        current === null
          ? current
          : current.map((item) => (item.id === id ? { ...item, read: true } : item)),
      );
      setUnreadCount((count) => Math.max(0, count - 1));
    }
    announcementsApi.markRead(id).catch(() => {
      // Not recorded: it's offered as unread again on the next load.
      readIds.current.delete(id);
    });
  }, []);

  // The pop-up: once, on this load, when no other dialog is open.
  const candidate =
    locked || popupId === null || items === null
      ? null
      : (items.find((item) => item.id === popupId && !item.read) ?? null);
  useEffect(() => {
    if (candidate === null || popupShown.current) return undefined;
    let observer: MutationObserver | null = null;
    const show = () => {
      if (popupShown.current || modalOpen()) return false;
      popupShown.current = true;
      setPopup(candidate);
      return true;
    };
    queueMicrotask(() => {
      if (show()) return;
      observer = new MutationObserver(() => {
        if (show()) observer?.disconnect();
      });
      // A modal kept mounted when closed only changes its class (`MuiModal-hidden`).
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    });
    return () => observer?.disconnect();
  }, [candidate]);

  const value = useMemo<AnnouncementsValue>(
    () => ({
      items,
      unreadCount,
      error,
      reload: () => void load(),
      markRead,
      openWhatsNew: () => setWhatsNew(true),
    }),
    [items, unreadCount, error, load, markRead],
  );

  function dismissPopup() {
    if (popup !== null) markRead(popup.id);
    setPopup(null);
  }

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
      {whatsNew && (
        <Dialog
          open
          onClose={() => setWhatsNew(false)}
          aria-labelledby={WHATS_NEW_TITLE_ID}
          fullWidth
          maxWidth="sm"
          fullScreen={fullScreen}
          data-whats-new=""
        >
          <DialogTitle id={WHATS_NEW_TITLE_ID}>{t('support.announcements.whatsNew')}</DialogTitle>
          <DialogContent>
            <AnnouncementList announcements={value} />
          </DialogContent>
          <DialogActions>
            <DialogDoneButton onClick={() => setWhatsNew(false)} />
          </DialogActions>
        </Dialog>
      )}
      {popup !== null && (
        <Dialog
          open
          onClose={dismissPopup}
          aria-labelledby={POPUP_TITLE_ID}
          fullWidth
          maxWidth="sm"
          fullScreen={fullScreen}
          data-announcement-popup=""
        >
          <DialogTitle id={POPUP_TITLE_ID}>{popup.title}</DialogTitle>
          <DialogContent>
            <AnnouncementBody announcement={popup} hideTitle />
          </DialogContent>
          <DialogActions>
            <DialogDoneButton onClick={dismissPopup} />
          </DialogActions>
        </Dialog>
      )}
    </AnnouncementsContext.Provider>
  );
}
